import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Flame, Lock, Sparkles } from "lucide-react";
import { api } from "@/lib/auth";
import { localDay, useQuantumJourney, useToday } from "@/lib/quantum";
import { useSpace } from "@/lib/space";
import BodyMap, { REGION_LABELS } from "@/components/quantum/BodyMap";
import Seal from "@/components/quantum/Seal";

// Shared by both apps' 13-gate journeys: today's practice + streak, the gate list, and the field of practice.
const Calendar = ({ days }) => {
  const set = new Set(days);
  const cells = Array.from({ length: 28 }, (_, k) => {
    const d = new Date();
    d.setDate(d.getDate() - (27 - k));
    return localDay(d);
  });
  return (
    <div data-testid="qi-calendar" className="mt-4 grid grid-cols-7 gap-1.5" aria-label="Practice days in the last four weeks">
      {cells.map((d) => <span key={d} title={d} data-on={set.has(d) ? "yes" : "no"} className={`aspect-square rounded-[3px] ${set.has(d) ? "bg-[#C2414F] shadow-[0_0_8px_rgba(194,65,79,0.6)]" : "bg-[#D9B47A]/10"} ${d === localDay() ? "ring-1 ring-[#D9B47A]" : ""}`} />)}
    </div>
  );
};

export const TodayCard = ({ label = "Today’s practice" }) => {
  const t = useToday();
  const space = useSpace();
  if (!t) return null;
  const s = t.suggestion;
  return (
    <section data-testid="qi-today" className="mt-6 border border-[#E8838E]/30 bg-[#140910] p-6">
      <div className="flex items-center justify-between gap-3">
        <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#E8838E]">{label}</p>
        <p data-testid="qi-streak" className="inline-flex items-center gap-1.5 font-tt-mono text-[12px] text-[#D9B47A]"><Flame className="w-4 h-4" /> {t.streak} day{t.streak === 1 ? "" : "s"}</p>
      </div>
      {s ? (
        <Link to={`${space.gate(s.g)}?practice=${s.p}`} data-testid="qi-today-practice" className="group mt-4 block">
          <p className="font-display text-2xl leading-snug">{s.title}</p>
          <p className="mt-1 text-[14px] text-[#A8998C]">Gate {s.g + 1} · {s.gate} · {s.mode === "partner" ? "with a partner" : "solo"} · about {s.minutes} min</p>
          <span className="mt-4 inline-flex items-center gap-2 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#D9B47A]">{t.practised_today ? "Practise again" : "Begin"} <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" /></span>
        </Link>
      ) : (
        <p className="mt-4 text-[15px] text-[#CFC2B6]">Your daily practice appears here once your first gate opens.</p>
      )}
      <Calendar days={t.days} />
      <p className="mt-2 text-[12px] text-[#A8998C]">Last four weeks · {t.total_days} practice day{t.total_days === 1 ? "" : "s"} in all</p>
    </section>
  );
};

export const NextGate = () => {
  const { gates } = useQuantumJourney();
  const space = useSpace();
  const next = gates.find((x) => x.published && !x.locked && !x.done);
  if (next) {
    return (
      <Link to={space.gate(next.g)} data-testid="quantum-continue" className="group mt-6 block border border-[#D9B47A]/25 p-6 hover:border-[#D9B47A]/60 transition-colors">
        <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#A8998C]">Your next gate · {String(next.g + 1).padStart(2, "0")}</p>
        <p className="mt-2 font-display text-3xl">{next.name}</p>
        <p className="mt-2 text-[15px] text-[#CFC2B6] line-clamp-2">{next.line}</p>
      </Link>
    );
  }
  if (!gates.length) return null;
  return (
    <p data-testid="quantum-waiting" className="mt-6 border border-[#D9B47A]/20 p-6 font-display italic text-2xl text-[#D9B47A]">
      {gates.some((x) => x.done) ? "You have completed every open gate. New gates will appear here." : "Ariel is preparing your gates. They will appear here very soon."}
    </p>
  );
};

export const GateList = () => {
  const { gates } = useQuantumJourney();
  const space = useSpace();
  return (
    <ol className="mt-8 border-t border-[#D9B47A]/15">
      {gates.map((x) => {
        const closed = !x.published || x.locked;
        return (
          <li key={x.g}>
            <Link to={space.gate(x.g)} data-testid={`quantum-gate-${x.g}`} data-state={x.done ? "done" : x.locked ? "locked" : !x.published ? "preparing" : "open"} className={`flex items-center gap-4 py-4 border-b border-[#D9B47A]/15 hover:bg-[#140910] transition-colors px-1 ${closed && !x.done ? "opacity-60" : ""}`}>
              <Seal g={x.g} earned={x.done} size={48} />
              <span className="min-w-0">
                <span className="block font-display text-xl leading-snug">{x.name}</span>
                <span className="block text-sm text-[#A8998C] truncate">{x.done ? "Sealed" : x.locked ? "Opens after the earlier gates" : !x.published ? "Being prepared" : `${x.practices} practice${x.practices === 1 ? "" : "s"} · teachings · reflection`}</span>
              </span>
              <span className="ml-auto shrink-0 text-[#8F8176]">{x.done ? <Check className="w-4 h-4 text-[#E8838E]" /> : x.locked ? <Lock className="w-4 h-4" /> : !x.published ? <Sparkles className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}</span>
            </Link>
          </li>
        );
      })}
    </ol>
  );
};

export const FieldView = ({ badges }) => {
  const { done } = useQuantumJourney();
  const space = useSpace();
  const t = useToday();
  const [f, setF] = useState(null);
  const [figure, setFigure] = useState(() => localStorage.getItem("qi-figure") || "female");
  useEffect(() => {
    api.get(`${space.api}/field`).then(({ data }) => setF({ entries: data?.entries ?? [], avg: data?.average_after ?? {} })).catch(() => setF({ entries: [], avg: {} }));
  }, [space.api]);
  const top = Object.entries(f?.avg ?? {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).slice(0, 3);
  return (
    <>
      <section className="mt-8 border border-[#D9B47A]/20 p-6">
        <BodyMap figure={figure} onFigure={(x) => { localStorage.setItem("qi-figure", x); setFigure(x); }} value={f?.avg ?? {}} testId="qi-field-heat" />
        <p data-testid="qi-field-summary" className="mt-5 text-[15px] text-[#CFC2B6]">
          {top.length ? <>Brightest after practice: {top.map(([k]) => REGION_LABELS[k]).join(", ")}.</> : "Map your body after a practice and your first glow will appear here."}
        </p>
        <p className="mt-1 text-[13px] text-[#A8998C]">{(f?.entries ?? []).length} practices logged</p>
      </section>
      <section className="mt-10">
        <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">Gate seals · {done.length}/13</p>
        <div data-testid="qi-seals" className="mt-4 grid grid-cols-4 sm:grid-cols-5 gap-3">
          {Array.from({ length: 13 }, (_, g) => <Seal key={g} g={g} earned={done.includes(g)} size={68} testId={`qi-seal-${g}`} />)}
        </div>
      </section>
      <section className="mt-10">
        <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">Badges</p>
        <ul data-testid="qi-badges" className="mt-4 grid grid-cols-2 gap-3">
          {badges.map(([k, label, test]) => {
            const got = Boolean(f && t && test(f, t, done.length));
            return (
              <li key={k} data-testid={`qi-badge-${k}`} data-earned={got ? "yes" : "no"} className={`border p-4 ${got ? "border-[#E8838E]/60 bg-[#140910]" : "border-[#D9B47A]/15 opacity-60"}`}>
                <Flame className={`w-4 h-4 ${got ? "text-[#E8838E]" : "text-[#8F8176]"}`} />
                <p className="mt-3 font-display text-lg leading-tight">{label}</p>
                <p className="mt-1 text-[12px] text-[#A8998C]">{got ? "Earned" : "Not yet"}</p>
              </li>
            );
          })}
        </ul>
      </section>
    </>
  );
};
